<script setup>
import { ref, onMounted, computed } from 'vue';
import { PlusIcon } from 'vue-tabler-icons';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();

const loading = ref(false);

const filters = computed(() => store.getters['ci/cItemsFilters']);
const resources = computed(() => store.getters['ci/cItems']);
const currentPage = computed(() => Math.floor(filters.value.offset / filters.value.limit) + 1);
const pages = computed(() => {
  if (!filters.value.total) {
    return [];
  }

  const total = Math.ceil(filters.value.total / filters.value.limit);
  const value = Array(total)
    .fill()
    .map((_, index) => index + 1)
    .filter((_, index) => index > currentPage.value - 4 && index < currentPage.value + 2);

  if (value[0] !== 1) {
    value.unshift('...');
    value.unshift(1);
  }
  if (value[value.length - 1] !== total) {
    value.push('...');
    value.push(total);
  }
  return value;
});

async function changePageByNumber(page = currentPage.value) {
  loading.value = true;
  store.commit('ci/setCIFilters', {
    offset: (page - 1) * filters.value.limit,
  });
  try {
    await store.dispatch('ci/fetchCItems');
  } finally {
    loading.value = false;
  }
}

onMounted(changePageByNumber);
</script>

<template>
  <section
    class="listing relative flex h-full w-full flex-col overflow-y-auto pb-12 text-black"
    :class="{ 'pointer-events-none': loading, 'opacity-60': loading }"
  >
    <div class="mb-4 h-full w-full overflow-y-auto shadow">
      <table class="w-full">
        <thead>
          <tr class="text-md border-t-2 text-left font-semibold uppercase tracking-wide text-brand-500">
            <th class="border-l border-r px-4 py-3">Namespace</th>
            <th class="border-l border-r px-4 py-3">Name</th>
            <th class="border-l border-r px-4 py-3">Created by</th>
            <th class="border-l border-r px-4 py-3">Created at</th>
            <th class="border-l border-r px-4 py-3">Updated at</th>
          </tr>
        </thead>
        <tbody class="bg-white">
          <tr
            v-for="resource in resources"
            :key="resource.shortId"
            role="link"
            data-href="`/configurable-items/${resource.namespace}:${resource.name}`"
            tabindex="0"
            class="cursor-pointer text-gray-700 hover:bg-blue-50"
            @click="router.push(`/configurable-items/${resource.namespace}:${resource.name}`)"
          >
            <td class="border px-4 py-3 text-sm font-semibold">{{ resource.namespace }}</td>
            <td class="border px-4 py-3 text-sm font-semibold">{{ resource.name }}</td>
            <td class="border px-4 py-3 text-xs">{{ resource.created_by }}</td>
            <td class="border px-4 py-3 text-xs">{{ new Date(resource.created_at).toLocaleString('en-us') }}</td>
            <td class="border px-4 py-3 text-xs">
              {{ new Date(resource.updated_at || resource.created_at).toLocaleString('en-us') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer class="absolute bottom-0 flex w-full items-center justify-center gap-4 bg-offwhite py-2 px-6">
      <div class="flex flex-grow justify-center text-black">
        <p id="page__total" class="py-2 text-sm text-black">{{ filters.total }} items</p>
        <div
          id="previousPage"
          class="mr-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-white"
          :class="{
            'pointer-events-none opacity-70': currentPage === 1,
          }"
          @click="changePageByNumber(currentPage - 1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-chevron-left h-6 w-6"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </div>
        <div class="flex h-8 rounded bg-white font-medium">
          <div
            v-for="page in pages"
            :id="`page__${page}`"
            :key="page"
            class="
              hidden
              w-8
              cursor-pointer
              items-center
              justify-center
              rounded
              leading-5
              transition
              duration-150
              ease-in
              md:flex
            "
            :class="{
              'pointer-events-none': page === '...',
              'pointer-events-none bg-brand-500 text-white': page === currentPage,
            }"
            @click="changePageByNumber(page)"
          >
            {{ page }}
          </div>
        </div>
        <div
          id="nextPage"
          class="ml-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-white"
          :class="{
            'pointer-events-none opacity-70': currentPage === pages[pages.length - 1],
          }"
          @click="changePageByNumber(currentPage + 1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-chevron-right h-6 w-6"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
      </div>

      <router-link
        :to="`/configurable-items/create`"
        class="
          flex
          items-center
          gap-2
          rounded
          border border-brand-500
          py-2
          px-3
          text-brand-500
          hover:border hover:border-brand-500 hover:bg-brand-500 hover:text-white
        "
      >
        <plus-icon class="h-4 w-4" /> Create CI
      </router-link>
    </footer>
  </section>
</template>
